/**
 * R-131-EPISTEMIC-GROUNDING-D — contract / document review negation classes.
 *
 * Class 1 (clause absence within a cited document) vs Class 2 (document or
 * fact absence across the corpus). Prompt scope gates which class is permitted.
 */
import type { CustomBriefQuestion, SiteRagReport } from '../types/siteRag';
import { customBriefQuestionSectionId } from './customBriefSections';

/** Prompt scope for contract-review negation rules. */
export type ContractReviewEpistemicScope = 'this-document' | 'across-corpus';

/** Known contract-review diligence surfaces. */
export type ContractReviewSurface =
  | 'constellation-epc'
  | 'ppa'
  | 'spa-sra'
  | 'gas-supply';

export type ContractReviewNegationClass = 'clause-absence' | 'document-fact-absence';

export const CONTRACT_REVIEW_PROMPT_VERSION = 'r131-epistemic-grounding-d-v1';

/** Class 1 — clause / provision absence within the document under review. */
export const CLAUSE_ABSENCE_PATTERNS: ReadonlyArray<RegExp> = [
  /\b(?:this|the)\s+(?:contract|agreement|ppa|epc|msa|spa|sra|gssa)\s+(?:does|do)\s+not\s+(?:contain|include)\b/i,
  /\b(?:this|the)\s+(?:\w+(?:\s+\w+){0,5})\s+(?:does|do)\s+not\s+contain\b/i,
  /\bdoes\s+not\s+(?:contain|include)\s+(?:a|an|any|the)\s+/i,
  /\b(?:is|are)\s+not\s+(?:included|present|addressed)\s+in\s+(?:this|the)\s+/i,
  /\bcontains?\s+no\s+(?:\w+\s+){0,4}(?:clause|provision|section|article|term|language)\b/i,
  /\blacks?\s+(?:a|an|any)\s+(?:\w+\s+){0,4}(?:clause|provision|section|article)\b/i,
  /\bno\s+\w+\s+clause\s+(?:is\s+)?(?:included|present|in)\b/i,
];

const SURFACE_PATTERNS: ReadonlyArray<{ surface: ContractReviewSurface; re: RegExp }> = [
  { surface: 'constellation-epc', re: /\b(?:constellation|epc\s+wrap|epc\s+scope|engineering,\s*procurement)\b/i },
  { surface: 'ppa', re: /\b(?:ppa|power\s+purchase|offtake|tolling)\b/i },
  { surface: 'spa-sra', re: /\b(?:spa|sra|slot\s+reservation|purchase\s+agreement)\b/i },
  { surface: 'gas-supply', re: /\b(?:gas\s+supply|gssa|fuel\s+supply|pipeline\s+agreement|gas\s+transport)\b/i },
];

const ACROSS_CORPUS_QUESTION_PATTERNS: ReadonlyArray<RegExp> = [
  /\bdo\s+we\s+have\b/i,
  /\bis\s+there\s+(?:a|an|the)\b/i,
  /\bhave\s+(?:we|the\s+parties)\b/i,
  /\bhas\s+(?:the|a|an)\s+\w+\s+been\b/i,
  /\b(?:no|not)\s+\w+\s+exists\b/i,
  /\bexists?\s+between\s+the\s+parties\b/i,
  /\bnot\s+yet\s+(?:executed|signed|formed|binding|firm)\b/i,
];

const THIS_DOCUMENT_QUESTION_PATTERNS: ReadonlyArray<RegExp> = [
  /\bin\s+(?:the|this)\s+(?:\w+\s+){0,4}(?:agreement|contract|msa|ppa|epc|spa|sra|gssa|document)\b/i,
  /\b(?:review|reviewing|clause|provision|section|article|term|scope|wrap)\b/i,
  /\b(?:this|the)\s+(?:\w+\s+){0,3}(?:agreement|contract|msa|ppa|epc|spa|sra)\b/i,
];

export function detectContractReviewSurface(text: string): ContractReviewSurface | null {
  for (const { surface, re } of SURFACE_PATTERNS) {
    if (re.test(text)) return surface;
  }
  return null;
}

/** Infer prompt scope from diligence question / surface context. */
export function inferContractReviewScope(
  text: string,
  surface?: ContractReviewSurface | null,
): ContractReviewEpistemicScope {
  const t = text.trim();
  if (!t) return 'across-corpus';

  if (ACROSS_CORPUS_QUESTION_PATTERNS.some((re) => re.test(t))) {
    return 'across-corpus';
  }
  if (THIS_DOCUMENT_QUESTION_PATTERNS.some((re) => re.test(t))) {
    return 'this-document';
  }

  if (surface === 'constellation-epc' && /\b(?:scope|wrap|terms|clause|provision)\b/i.test(t)) {
    return 'this-document';
  }

  return 'across-corpus';
}

export function classifyContractReviewNegation(text: string): ContractReviewNegationClass | null {
  const trimmed = text.trim();
  if (!trimmed) return null;

  if (CLAUSE_ABSENCE_PATTERNS.some((re) => re.test(trimmed))) {
    return 'clause-absence';
  }

  const documentFactPatterns: RegExp[] = [
    /(?:has\s+not\s+been|is\s+not\s+yet)\s+\w+/i,
    /\b(?:the\s+)?lack\s+of\s+/i,
    /\bno\s+(?:[A-Z][A-Za-z]*\s+)?(?:exists|is\s+in\s+place|is\s+signed|has\s+been)/i,
    /\bnot\s+yet\s+(?:executed|signed|formed|established|binding|firm)/i,
    /\bstill\s+in\s+discussion\b/i,
    /\byet\s+to\s+be\b/i,
    /\bremains\s+unresolved\b/i,
  ];
  if (documentFactPatterns.some((re) => re.test(trimmed))) {
    return 'document-fact-absence';
  }

  return null;
}

export function buildContractReviewEpistemicBlock(
  scope: ContractReviewEpistemicScope,
  surface?: ContractReviewSurface | null,
): string {
  const surfaceLine = surface
    ? `Surface: ${surface.replace('-', ' ')}. `
    : '';

  return `---
CONTRACT-REVIEW EPISTEMIC SCOPE (R-131-EPISTEMIC-GROUNDING-D — ${scope}):
${surfaceLine}Two negation classes apply to contract / agreement diligence:

Class 1 — Clause absence WITHIN the document under review (permitted only when scope='this-document'):
  Example: "This MSA does not contain a force majeure clause."
  Cite the document being reviewed itself as evidence.

Class 2 — Document or fact absence ACROSS the corpus (dangerous if uncited):
  Examples: "No MSA exists between the parties." / "The PPA has not been executed."
  Permitted ONLY when external documentation explicitly states the absence with [Ref N].

When scope='this-document': Class 1 is permitted with the reviewed document as citation.
When scope='across-corpus': ONLY Class 2 with explicit absence citation is permitted — do NOT emit Class 1 clause-absence claims unless a cited document is the subject of the question.
---`;
}

export function buildContractReviewScopeLines(
  questions: readonly CustomBriefQuestion[],
): string[] {
  const lines: string[] = ['Contract-review epistemic scope (per question):'];
  for (const q of questions) {
    if (!q.text.trim()) continue;
    const surface = detectContractReviewSurface(q.text);
    const scope = inferContractReviewScope(q.text, surface);
    const surfaceTag = surface ? ` · ${surface}` : '';
    lines.push(`  [${q.id}] scope=${scope}${surfaceTag} — ${q.text.trim()}`);
  }
  return lines;
}

/** Stamp sectionId → scope for custom-brief question sections at gen time. */
export function buildCustomBriefContractReviewScopes(
  questions: readonly CustomBriefQuestion[],
): Record<string, ContractReviewEpistemicScope> {
  const out: Record<string, ContractReviewEpistemicScope> = {};
  for (const q of questions) {
    if (!q.text.trim() || !q.id.trim()) continue;
    const surface = detectContractReviewSurface(q.text);
    out[customBriefQuestionSectionId(q.id.trim())] = inferContractReviewScope(q.text, surface);
  }
  return out;
}

export function resolveContractReviewScopeForSection(
  sectionId: string,
  report: SiteRagReport,
  fallback: ContractReviewEpistemicScope = 'across-corpus',
): ContractReviewEpistemicScope {
  const bySection = report.contractReviewEpistemicScopesBySection;
  if (bySection?.[sectionId]) return bySection[sectionId];

  if (report.contractReviewEpistemicScope) {
    return report.contractReviewEpistemicScope;
  }

  return fallback;
}

/** Whether the detector should suppress a block finding for this candidate. */
export function shouldSuppressAbsenceFinding(
  negationClass: ContractReviewNegationClass,
  scope: ContractReviewEpistemicScope,
): boolean {
  return negationClass === 'clause-absence' && scope === 'this-document';
}

/** Whether the detector should force a block finding before citation check. */
export function shouldForceAbsenceFinding(
  negationClass: ContractReviewNegationClass,
  scope: ContractReviewEpistemicScope,
): boolean {
  return negationClass === 'clause-absence' && scope === 'across-corpus';
}
